import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { NotificationsIcon, NotificationsNoneIcon as NotificationsOffIcon } from '@atlas/ui';
import type { NotificationItem } from '@atlas/shared';
import {
  notificationHref,
  notificationVerb,
  useMarkAllRead,
  useMarkRead,
  useNotificationList,
  useUnreadCount,
} from '../../services/notifications';

const RECENT_LIMIT = 10;

interface NotificationBellProps {
  /** Optional override for the IconButton's aria-label (defaults to a count-aware string). */
  ariaLabel?: string;
  /** Fires every time the unread count changes — for downstream chrome that wants its own indicator. */
  onActivity?: (count: number) => void;
}

/**
 * Persistent toolbar bell. Renders into the `[data-slot="notification-bell"]`
 * placeholder in `Layout.tsx`.
 *
 * Behavior summary:
 *  - Single MUI `<IconButton>` with a `<Badge>` showing the unread count
 *    (rendered with `showZero={false}` so the dot only appears when needed).
 *  - Click opens a Popover with the most recent ~10 notifications and a
 *    "View all" link to `/notifications`.
 *  - Each row links to the referenced entity and marks the notification read
 *    on click.
 *  - Unread count is live via `mention:new`; falls back to 60s polling.
 *  - `aria-label` follows the contract: defaults to "Notifications" with
 *    "N unread" when applicable. `aria-haspopup="menu"` for screen readers.
 *  - Min touch target 44x44 enforced via `minWidth`/`minHeight` on the button.
 */
export function NotificationBell({ ariaLabel, onActivity }: NotificationBellProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;

  // Notify parent / future shell when count changes.
  useOnActivity(unreadCount, onActivity);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const ariaLabelFinal =
    ariaLabel ??
    (unreadCount > 0
      ? t('notifications.bellAriaLabelWithCount', { count: unreadCount })
      : t('notifications.bellAriaLabel'));

  return (
    <>
      <Tooltip title={unreadCount > 0 ? t('notifications.tooltipWithCount', { count: unreadCount }) : t('notifications.tooltipNoUnread')}>
        <IconButton
          ref={anchorRef}
          onClick={handleOpen}
          size="small"
          aria-label={ariaLabelFinal}
          aria-haspopup="menu"
          aria-expanded={open || undefined}
          sx={{
            color: 'text.secondary',
            // Min touch target — 44x44 per task spec / WCAG 2.5.5
            minWidth: 44,
            minHeight: 44,
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            overlap="circular"
            // Hide entirely when zero so the icon doesn't carry a stale "0".
            invisible={unreadCount === 0}
            aria-hidden={unreadCount === 0 ? true : undefined}
          >
            {unreadCount > 0 ? (
              <NotificationsIcon fontSize="small" />
            ) : (
              <NotificationsOffIcon fontSize="small" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: '92vw', maxHeight: '70vh' },
            'aria-label': t('notifications.panelAriaLabel'),
            role: 'menu',
          },
        }}
      >
        <NotificationPanel
          unreadCount={unreadCount}
          onClose={handleClose}
          onViewAll={() => {
            handleClose();
            navigate('/notifications');
          }}
        />
      </Popover>
    </>
  );
}

interface NotificationPanelProps {
  unreadCount: number;
  onClose: () => void;
  onViewAll: () => void;
}

function NotificationPanel({ unreadCount, onClose, onViewAll }: NotificationPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useNotificationList({
    limit: RECENT_LIMIT,
    unreadOnly: true,
  });
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = data ?? [];

  const handleItemClick = (n: NotificationItem) => {
    if (!n.read) {
      markRead.mutate(n.id);
    }
    const href = notificationHref(n);
    onClose();
    if (href) {
      navigate(href);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
          {t('notifications.title')}
        </Typography>
        <Button
          size="small"
          variant="text"
          disabled={unreadCount === 0 || markAllRead.isPending}
          onClick={() => markAllRead.mutate()}
          sx={{ textTransform: 'none' }}
        >
          {t('notifications.markAllRead')}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} aria-label={t('notifications.loadingAriaLabel')} />
        </Box>
      )}

      {isError && !isLoading && (
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.loadError')}
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.allCaughtUp')}
          </Typography>
        </Box>
      )}

      {!isLoading && items.length > 0 && (
        <MenuList dense disablePadding>
          {items.map((n) => (
            <NotificationRow
              key={n.id}
              item={n}
              onClick={() => handleItemClick(n)}
            />
          ))}
        </MenuList>
      )}

      <Divider />
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none' }}>
          {t('notifications.viewAll')}
        </Button>
      </Box>
    </Box>
  );
}

interface NotificationRowProps {
  item: NotificationItem;
  onClick: () => void;
}

function NotificationRow({ item, onClick }: NotificationRowProps) {
  const isUnread = !item.read;

  return (
    <MenuItem
      onClick={onClick}
      sx={{
        py: 1,
        // Visible unread treatment without leaning on color alone.
        bgcolor: isUnread ? 'action.hover' : 'transparent',
        whiteSpace: 'normal',
        alignItems: 'flex-start',
      }}
      role="menuitem"
    >
      {isUnread && (
        <Box
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            mt: 0.75,
            mr: 1.25,
            flexShrink: 0,
          }}
        />
      )}
      {!isUnread && <Box aria-hidden sx={{ width: 8, mr: 1.25, flexShrink: 0 }} />}
      <ListItemText
        primary={
          <Typography variant="body2" sx={{ fontWeight: isUnread ? 600 : 400 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {item.authorDisplayName}
            </Box>
            {' '}
            {notificationVerb(item)}
          </Typography>
        }
        secondary={
          <>
            {item.body && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {truncate(item.body, 120)}
              </Typography>
            )}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {formatRelativeTime(item.createdAt)}
            </Typography>
          </>
        }
      />
    </MenuItem>
  );
}


function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + '…';
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Local effect — onActivity is optional.
function useOnActivity(count: number, cb?: (count: number) => void) {
  useEffect(() => {
    if (cb) cb(count);
  }, [count, cb]);
}

export default NotificationBell;
